import { useState } from "react";
import { assignUserRole, registerWaiterUser } from "../../services/auth";
import { SALON_ORDER_ROLE_ID } from "../../services/checkRole";

const ERROR_TEXT: Record<string, string> = {
  AgeRestriction: "Garson 13 yaşından büyük olmalı.",
  EmailAddressError: "Geçerli bir e-posta adresi girin.",
  WaiterRestaurantNotResolved: "Hesabınıza bağlı bir restoran bulunamadı; garson hangi restorana açılacağı belirlenemiyor.",
  WaiterRestaurantAmbiguous: "Hesabınız birden fazla restorana bağlı; garsonun hangi restorana açılacağı belirlenemiyor.",
  StaffRestaurantNotFound: "Bu restorana garson açma yetkiniz yok (restoran hesabınıza bağlı değil ya da bağ kapalı).",
  UserAlreadyExists: "Bu e-posta ya da telefon numarasıyla bir hesap zaten var.",
  EmailAlreadyExists: "Bu e-posta adresi zaten kayıtlı.",
  PhoneAlreadyExists: "Bu telefon numarası zaten kayıtlı.",
};

interface Props {
  /** Garsonun açılacağı restoran (salon ekranında o an görüntülenen). Çok restoranlı yöneticide zorunlu. */
  restaurantId: string;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateWaiterModal({ restaurantId, onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Hesap açıldı ama rol verilemediyse ikinci kez gönderip mükerrer hesap açılmasın
  const [created, setCreated] = useState(false);

  const handleSubmit = () => {
    if (name.trim().length < 2) { setError("Ad en az 2 karakter olmalı."); return; }
    if (surname.trim().length < 2) { setError("Soyad en az 2 karakter olmalı."); return; }
    if (phone.trim().length < 5) { setError("Geçerli bir telefon numarası girin."); return; }
    if (!email.trim()) { setError("E-posta zorunludur."); return; }
    if (!birthDate) { setError("Doğum tarihi zorunludur."); return; }
    if (password.length < 6) { setError("Parola en az 6 karakter olmalı."); return; }

    setSaving(true);
    setError("");
    registerWaiterUser({
      customerName: name.trim(),
      customerSurName: surname.trim(),
      customerTelNo: phone.trim(),
      customerEmail: email.trim(),
      customerBirthDate: new Date(birthDate).toISOString(),
      customerIsEnabled: true,
      customerLogin: { customerPassword: password },
    }, restaurantId)
      .then(async (res) => {
        // 18 Eki'den beri garson rolü tek başına salonu açmıyor; Salon Siparişi rolü de verilmeli.
        // Restoran yöneticisi bu rolü kendi personeline verebilir (UserRole beyaz listesi).
        const newUserId = res.data?.id;
        try {
          if (newUserId) await assignUserRole(newUserId, SALON_ORDER_ROLE_ID);
          onCreated();
          onClose();
        } catch (err: any) {
          onCreated();
          setCreated(true);
          const desc = Array.isArray(err?.response?.data) ? err.response.data[0]?.description : undefined;
          setError(`Garson hesabı oluşturuldu ancak Salon Siparişi yetkisi verilemedi${desc ? `: ${desc}` : "."} Bu yetki olmadan garson salona giremez; sistem yöneticisinden yetkiyi isteyin.`);
        }
      })
      .catch((err) => {
        const data = err?.response?.data;
        const code = Array.isArray(data) ? data[0]?.code : undefined;
        const desc = Array.isArray(data) ? data[0]?.description : undefined;
        setError(ERROR_TEXT[code ?? ""] ?? desc ?? "Garson hesabı oluşturulamadı.");
      })
      .finally(() => setSaving(false));
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", boxSizing: "border-box", padding: "9px 12px",
    borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: ".88rem", outline: "none",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: ".78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5,
  };

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="dinein-modal dinein-modal--narrow">
        <div className="dinein-modal__head">
          <div>
            <div className="dinein-modal__title"><i className="bi bi-person-plus-fill me-2"></i>Yeni Garson</div>
            <div className="dinein-modal__sub">Hesap, yöneticisi olduğunuz restorana bağlanır</div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={saving}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-modal__body">
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 140px" }}>
              <label style={labelStyle}>Ad <span style={{ color: "#dc2626" }}>*</span></label>
              <input style={inputStyle} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} placeholder="Ahmet" />
            </div>
            <div style={{ flex: "1 1 140px" }}>
              <label style={labelStyle}>Soyad <span style={{ color: "#dc2626" }}>*</span></label>
              <input style={inputStyle} value={surname} maxLength={100} onChange={(e) => setSurname(e.target.value)} placeholder="Yılmaz" />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Telefon <span style={{ color: "#dc2626" }}>*</span></label>
            <input style={inputStyle} value={phone} maxLength={20} onChange={(e) => setPhone(e.target.value)} placeholder="+905551112233" />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>E-posta <span style={{ color: "#dc2626" }}>*</span></label>
            <input type="email" style={inputStyle} value={email} maxLength={256} onChange={(e) => setEmail(e.target.value)} placeholder="ahmet@ornek.com" />
            <div className="dinein-hint" style={{ marginTop: 4 }}>Garson bu e-posta ve parolayla giriş yapar.</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Doğum Tarihi <span style={{ color: "#dc2626" }}>*</span></label>
            <input type="date" style={inputStyle} value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Parola <span style={{ color: "#dc2626" }}>*</span></label>
            <div style={{ position: "relative" }}>
              <input type={showPassword ? "text" : "password"} style={{ ...inputStyle, paddingRight: 38 }}
                value={password} maxLength={128} onChange={(e) => setPassword(e.target.value)} placeholder="En az 6 karakter" />
              <button type="button" onClick={() => setShowPassword((v) => !v)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}>
                <i className={`bi bi-eye${showPassword ? "-slash" : ""}`}></i>
              </button>
            </div>
          </div>

          {error && (
            <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
          )}
        </div>

        <div className="dinein-modal__foot">
          <button className="dinein-btn dinein-btn--ghost" onClick={onClose} disabled={saving}>{created ? "Kapat" : "Vazgeç"}</button>
          <button className="dinein-btn dinein-btn--primary" onClick={handleSubmit} disabled={saving || created}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Oluşturuluyor...</>
              : <><i className="bi bi-check-lg me-1"></i>Garsonu Oluştur</>}
          </button>
        </div>
      </div>
    </div>
  );
}
